import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { ListRow, ListRowsGroup } from '../components/list/list-api.ts';
import type { ListFindNext } from '../components/list/list-view.tsx';
import { Switch } from '../components/ui/switch.tsx';
import { NoticeCard } from '../components/ui/notice-card.tsx';
import { UNDO_SECONDS, UndoToast } from '../components/undo-toast.tsx';
import { apiFetch, ApiProblem, type OrderDetail } from '../lib/api.ts';
import { findNextCase, readCaseJump, writeCaseJump, type CaseJump } from '../lib/case-jump.ts';
import { useSession } from '../lib/session.tsx';

/**
 * Klärfälle nacheinander lösen (epic-auftragsampel Story 7.20, EXPERIENCE Flow F1): Speicher im Modul, weil der Datensatz
 * (`OrderRecord`, je Auftrag gekeyt) beim Sprung zum nächsten Klärfall neu aufgebaut wird – das Rückgängig-Angebot (10 s,
 * Story 7.9) muss den Wechsel überdauern und nach „Rückgängig“ zum gelösten Auftrag zurückführen. Dazu „Alles geklärt“,
 * die zuletzt gezeigten Aufträge (Rücksprung) und die Ansicht, aus der am Handy geöffnet wurde. Reine Oberfläche: keine
 * Schreibaktion außer den Rückgängig-Aufrufen der Aktionen selbst.
 */

interface FlowUndo {
  key: number;
  /** Person, die die Aktion ausgelöst hat; nur sie sieht und löst das Angebot aus. */
  owner: string;
  orderId: string;
  text: string;
  /** `null` = nur Bestätigung ohne „Rückgängig“ (Ticket 42: „Freigegeben“ mit Teillieferung). */
  undo: (() => Promise<void>) | null;
  /** Ende der 10 s ab dem Angebot (für eine neu eingehängte Einblendung, z. B. Handy zurück zur Liste). */
  endsAt: number;
  /**
   * Hartes Ende nach der Frist des Servers (Browser-Zeit aus der Restzeit, Story 9.35); danach verschwindet die Einblendung
   * auch bei Hover. Fehlt es, gelten nur die 10 s.
   */
  until?: Date;
}

export interface OrderFlowState {
  undo: FlowUndo | null;
  busy: boolean;
  /** Fehler eines Rückgängig; steht am betroffenen Auftrag. */
  error: { orderId: string; text: string } | null;
  /** Nach dem letzten gelösten Klärfall: „Alles geklärt“ über der Liste. */
  allClear: boolean;
}

const EMPTY: OrderFlowState = { undo: null, busy: false, error: null, allClear: false };
let state: OrderFlowState = EMPTY;
let nextKey = 1;
const listeners = new Set<() => void>();

/**
 * Person, der der Speicher gehört (Benutzer-ID), wie das Listen-Gedächtnis (`list-memory.ts`): Meldet sich in derselben
 * Browserseite jemand anderes an, beginnt er leer – kein Rückgängig-Angebot, kein Fehler, keine gemerkten Zeilen der
 * vorigen Person (Zweit-Review #557, Runde 5). Abmelden leert alles (`resetOrderFlow` über `clearPersonalCaches`).
 */
let owner: string | null = null;

function update(patch: Partial<OrderFlowState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Alles vergessen (Abmelden, Personenwechsel): Angebot, Fehler, „Alles geklärt“, gemerkte Zeilen, Handy-Abbild. */
export function resetOrderFlow(): void {
  owner = null;
  cancelCaseJumps();
  liveDeadline = null;
  clearTimeout(allClearTimer);
  listView = null;
  seenRows.clear();
  phoneView = null;
  editEntry = null;
  pendingSave = null;
  jumpChoice.clear();
  update(EMPTY);
}

/** Speicher der angemeldeten Person zuordnen; gehört er einer anderen, wird er vorher vollständig geleert. */
export function bindOrderFlowOwner(userId: string): void {
  if (owner === userId) return;
  resetOrderFlow();
  owner = userId;
}

/** Stand für `userId` – gehört der Speicher jemand anderem, ein leerer Stand. */
export function orderFlowFor(userId: string): OrderFlowState {
  return owner === userId ? state : EMPTY;
}

export function useOrderFlow(): OrderFlowState {
  const { me } = useSession();
  return useSyncExternalStore(
    subscribe,
    () => orderFlowFor(me.id),
    () => orderFlowFor(me.id),
  );
}

/**
 * Noch gültiges Angebot `key` für `userId` (nicht ersetzt, nicht verworfen, Speicher gehört dieser Person) – sonst `null`;
 * nur dann darf „Rückgängig“ etwas auslösen.
 */
export function liveOrderUndo(key: number, userId: string): FlowUndo | null {
  return owner === userId && state.undo?.key === key && state.undo.owner === userId ? state.undo : null;
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? err.detail : 'Etwas ist schiefgelaufen. Bitte noch einmal versuchen.';
}

/**
 * Rückgängig nach einer Aktion am Auftrag anbieten (ersetzt ein älteres Angebot); `userId` = Person, die die Aktion auslöste.
 * Zugeordnet wird der Speicher nur beim bestätigten Anmelden (`bindOrderFlowOwner`): Kommt das Angebot einer anderen Person
 * verspätet zurück (Aktion lief über die Anmeldung der nächsten Person hinaus), wird es verworfen – es leert weder deren
 * Speicher noch ihr Angebot und bricht keine laufende Sprung-Suche ab (Story 7.34).
 */
export function offerOrderUndo(userId: string, orderId: string, text: string, undo: (() => Promise<void>) | null, until?: Date): void {
  if (owner !== userId) return;
  cancelCaseJumps();
  update({
    undo: { key: nextKey++, owner: userId, orderId, text, undo, endsAt: Date.now() + UNDO_SECONDS * 1000, ...(until ? { until } : {}) },
    error: null,
    allClear: false,
  });
}

/**
 * Zähler der Sprung-Suchen: „Rückgängig“ (und jedes neue Angebot) erhöht ihn; eine Suche nach dem nächsten Klärfall, die
 * vor der Erhöhung begann, wird verworfen – sonst spränge das Panel nach „Rückgängig“ trotzdem weiter (Zweit-Review #557).
 */
let jumpGeneration = 0;

export function caseJumpGeneration(): number {
  return jumpGeneration;
}

function cancelCaseJumps(): void {
  jumpGeneration++;
}

export function clearOrderFlowError(): void {
  if (state.error) update({ error: null });
}

export function setAllClear(allClear: boolean): void {
  if (state.allClear !== allClear) update({ allClear });
}

/** Zuletzt gemeldete Ansicht der Auftragsliste (Reiter, Spalten, Filter, Suche, Sortierung). */
let listView: string | null = null;

/** „Alles geklärt“ gilt nur für die Ansicht, in der es entstand: bei einem Ansichtswechsel verschwindet es. */
export function noteListView(viewKey: string): void {
  if (listView !== null && listView !== viewKey) setAllClear(false);
  listView = viewKey;
}

// ---------------------------------------------------------------------------
// Zeilen der Liste

/** Zeile und Herkunftsgruppe eines gezeigten Auftrags (Rücksprung nach Rückgängig). */
export interface ShownRow {
  row: ListRow;
  group: ListRowsGroup | null;
}

/** Zuletzt gezeigte Aufträge je Schlüssel (Rücksprung nach Rückgängig, auch wenn die Zeile inzwischen aus dem Reiter fiel). */
const seenRows = new Map<string, ShownRow>();
const SEEN_ROWS_MAX = 2000;

export function rememberRow(row: ListRow, group: ListRowsGroup | null): void {
  if (seenRows.size > SEEN_ROWS_MAX) seenRows.clear();
  seenRows.set(row.key, { row, group });
}

export function rememberedRow(key: string): ShownRow | null {
  return seenRows.get(key) ?? null;
}

/**
 * Ansicht, aus der am Handy ein Auftrag geöffnet wurde (die Liste ist dann nicht eingehängt): die Server-Frage nach dem
 * nächsten Treffer derselben Listenabfrage und je erreichtem Auftrag seine Gruppe. Ein Auftrag, der nicht aus dieser
 * Ansicht geöffnet bzw. angesprungen wurde (z. B. per Link), springt nicht weiter.
 */
let phoneView: { findNext: ListFindNext; groups: Map<string, { group: ListRowsGroup | null; position: string | undefined }> } | null = null;

/** `position`: Stelle der Zeile in der Liste beim Öffnen (vor der lösenden Aktion, `ListRow.position`). */
export function setPhoneView(orderId: string, findNext: ListFindNext | undefined, group: ListRowsGroup | null, position: string | undefined): void {
  phoneView = findNext ? { findNext, groups: new Map([[orderId, { group, position }]]) } : null;
}

/** Server-Frage, Herkunftsgruppe und Stelle für `orderId` – `null`, wenn er nicht aus der gemerkten Ansicht kam. */
export function phoneViewFor(orderId: string): { findNext: ListFindNext; group: ListRowsGroup | null; position: string | undefined } | null {
  const at = phoneView?.groups.get(orderId);
  if (!phoneView || !at) return null;
  return { findNext: phoneView.findNext, group: at.group, position: at.position };
}

/**
 * Vollansicht eingehängt (Story 7.30): Wer sie verlässt (Liste, Suche, Link), nimmt die gemerkte Ansicht mit – ein später per
 * Link geöffneter Auftrag springt nie nach der alten Listenabfrage. Das Leeren wartet einen Takt, damit der Doppel-Aufruf der
 * Effekte (StrictMode) und der Wechsel von Auftrag zu Auftrag (gleiche Seite) die Ansicht nicht verwerfen.
 */
let orderPages = 0;

export function enterOrderPage(): () => void {
  orderPages++;
  return () => {
    orderPages--;
    window.setTimeout(() => {
      // Story 9.34: auf dem Weg in die Maske „Bearbeiten“ (Chunk lädt evtl. noch) bleibt die Ansicht stehen.
      if (orderPages === 0 && editEntry === null) phoneView = null;
    }, 0);
  };
}

// ---------------------------------------------------------------------------
// Maske „Bearbeiten“ (Story 9.34): Rückweg und Klärfall-Sprung nach dem Speichern

/**
 * Woher die Maske geöffnet wurde (Auftragsseite oder Seitenpanel). Solange gesetzt, hält sie die gemerkte Ansicht der Liste
 * (Klärfall-Sprung nach dem Speichern); die Maske liest sie beim Öffnen und meldet sich dann selbst als Auftragsseite an.
 */
let editEntry: { orderId: string; from: 'page' | 'panel' } | null = null;

export function noteOrderEditOpen(orderId: string, from: 'page' | 'panel'): void {
  editEntry = { orderId, from };
}

/** Herkunft der Maske für `orderId` (`null` = direkt geöffnet, z. B. per Link). */
export function orderEditEntry(orderId: string): 'page' | 'panel' | null {
  return editEntry?.orderId === orderId ? editEntry.from : null;
}

/** Maske eingehängt: zählt wie eine Auftragsseite (hält die gemerkte Ansicht), die Herkunft ist gelesen. */
export function enterOrderEdit(): () => void {
  const leave = enterOrderPage();
  editEntry = null;
  return leave;
}

/**
 * „Speichern ausstehend“: Stand des Auftrags vor dem Speichern in der Maske (`seen` = `dataUpdatedAt` des Auftrags im
 * Cache, `wasCase` = Pille „Klärfall“). Die Auftragsansicht übernimmt die Marke beim Einhängen; ist die Pille nach dem
 * Neuladen nicht mehr „Klärfall“, gilt der Klärfall als gelöst (Story 7.20) – wie nach einer Aktion direkt am Auftrag.
 */
let pendingSave: { orderId: string; wasCase: boolean; seen: number } | null = null;

export function notePendingSave(orderId: string, wasCase: boolean, seen: number): void {
  pendingSave = { orderId, wasCase, seen };
}

/** Marke für `orderId` abholen (einmalig). */
export function takePendingSave(orderId: string): { wasCase: boolean; seen: number } | null {
  if (pendingSave?.orderId !== orderId) return null;
  const { wasCase, seen } = pendingSave;
  pendingSave = null;
  return { wasCase, seen };
}

/** Angesprungener Auftrag gehört zur selben Ansicht (mit seiner Gruppe und Stelle). */
export function notePhoneTarget(orderId: string, group: ListRowsGroup | null, position: string | undefined): void {
  phoneView?.groups.set(orderId, { group, position });
}

// ---------------------------------------------------------------------------
// Schalter „Nach dem Lösen zum nächsten Klärfall“ (Browser, je Person, Standard ein)

const jumpChoice = new Map<string, boolean>();
const jumpListeners = new Set<() => void>();

export function caseJumpOn(userId: string): boolean {
  if (!jumpChoice.has(userId)) jumpChoice.set(userId, readCaseJump(userId));
  return jumpChoice.get(userId)!;
}

function setCaseJump(userId: string, on: boolean): void {
  // Ausschalten verwirft eine schon laufende Sprung-Suche (Zweit-Review #557): ihr Ergebnis wird nicht mehr angewendet.
  if (!on) cancelCaseJumps();
  jumpChoice.set(userId, on);
  writeCaseJump(userId, on);
  for (const l of jumpListeners) l();
}

export function CaseJumpSwitch() {
  const { me } = useSession();
  const labelId = useId();
  const on = useSyncExternalStore(
    (l) => {
      jumpListeners.add(l);
      return () => jumpListeners.delete(l);
    },
    () => caseJumpOn(me.id),
    () => true,
  );
  return (
    <div className="flex min-w-0 items-center gap-space-3" data-testid="klaerfall-springen">
      <Switch checked={on} onCheckedChange={(next) => setCaseJump(me.id, next)} aria-labelledby={labelId} />
      <span id={labelId} className="type-meta min-w-0 text-ink-2">
        Nach dem Lösen zum nächsten Klärfall
      </span>
    </div>
  );
}

let allClearTimer: number | undefined;

/**
 * „Alles geklärt“ gilt, solange die Auftragsliste eingehängt bleibt: beim Verlassen der Seite verschwindet es (verzögert,
 * damit das doppelte Einhängen im Entwicklungsmodus es nicht sofort löscht).
 */
export function useAllClearLifetime(): void {
  useEffect(() => {
    window.clearTimeout(allClearTimer);
    return () => {
      allClearTimer = window.setTimeout(() => setAllClear(false), 0);
    };
  }, []);
}

/** „Alles geklärt“ über der Liste (nach dem letzten gelösten Klärfall der Ansicht). */
export function AllClearNotice() {
  const { allClear } = useOrderFlow();
  if (!allClear) return null;
  // Ticket 1.272 (S8, Vorbild Superhuman „Inbox Zero“): Hinweis-Karte „Bereit“ mit Haken, der in `motion-base` einblendet
  // (bei „Bewegung reduzieren“ sofort); der Satz bleibt wörtlich.
  return (
    <div role="status">
      <NoticeCard
        tone="ready"
        testId="alle-klaerfaelle-erledigt"
        title="Alle Klärfälle erledigt"
        mark={<CircleCheck aria-hidden data-mark="haken" size={18} strokeWidth={2} className="flex-none animate-fade-in" />}
      >
        <span data-testid="alles-geklaert">Alles geklärt – in dieser Ansicht ist kein Klärfall mehr offen.</span>
      </NoticeCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rückgängig-Einblendung über den Auftragswechsel hinweg

/**
 * Rückgängig-Einblendung des zuletzt gelösten Auftrags (UndoToast, 10 s). Nach „Rückgängig“ (auch wenn es
 * scheitert) führt `onReturn` zum Auftrag zurück.
 */
export function OrderFlowUndo({ onReturn }: { onReturn?: (orderId: string) => void }) {
  const { me } = useSession();
  const flow = useOrderFlow();
  if (!flow.undo || flow.undo.owner !== me.id) return null;
  return <FlowToast key={flow.undo.key} offer={flow.undo} busy={flow.busy} onReturn={onReturn} />;
}

/**
 * Ende des Angebots, wie es die zuletzt eingehängte Einblendung gezählt hat (die Pause bei Hover und Fokus verlängert es);
 * ohne Einblendung läuft die Zeit weiter.
 */
let liveDeadline: { key: number; endsAt: number } | null = null;

function deadlineOf(offer: FlowUndo): number {
  return liveDeadline?.key === offer.key ? liveDeadline.endsAt : offer.endsAt;
}

function FlowToast({ offer, busy, onReturn }: { offer: FlowUndo; busy: boolean; onReturn: ((orderId: string) => void) | undefined }) {
  // Restzeit beim Einhängen (z. B. nach dem Sprung oder der Rückkehr zur Liste); die Einblendung zählt selbst weiter.
  // Story 9.35: höchstens bis zum Fristende des Servers (`until`).
  const [seconds] = useState(() => Math.min(UNDO_SECONDS, (deadlineOf(offer) - Date.now()) / 1000, offer.until ? (offer.until.getTime() - Date.now()) / 1000 : Infinity));
  const expired = seconds <= 0;
  const left = useRef(Math.max(0, seconds * 1000));
  // Abgelaufen (Seite verlassen und später zurück): Angebot verwerfen statt erneut anzeigen.
  useEffect(() => {
    if (expired && state.undo?.key === offer.key) update({ undo: null });
  }, [expired, offer.key]);
  useEffect(
    () => () => {
      if (left.current > 0) liveDeadline = { key: offer.key, endsAt: Date.now() + left.current };
    },
    [offer.key],
  );
  const run = useCallback(async () => {
    // Nur ein noch gültiges Angebot (nicht abgelaufen, nicht ersetzt, von dieser Person) macht etwas rückgängig.
    if (!offer.undo || expired || left.current <= 0 || !liveOrderUndo(offer.key, offer.owner)) return;
    const undo = offer.undo;
    cancelCaseJumps();
    update({ busy: true });
    try {
      await undo();
    } catch (err) {
      // Inzwischen abgemeldet oder Person gewechselt: der Fehler gehört nicht in den Speicher der nächsten Person.
      if (owner === offer.owner) update({ error: { orderId: offer.orderId, text: problemText(err) } });
    } finally {
      // Während des Aufrufs neu angeboten (weitere Aktion, z. B. am nächsten Auftrag): deren Angebot bleibt stehen, kein
      // Rücksprung zum älteren Auftrag (Zweit-Review #557).
      if (state.undo?.key === offer.key) {
        update({ busy: false, undo: null, allClear: false });
        onReturn?.(offer.orderId);
      } else update({ busy: false });
    }
  }, [offer, onReturn, expired]);
  if (expired) return null;
  return (
    <UndoToast
      message={offer.text}
      busy={busy}
      seconds={seconds}
      {...(offer.until ? { until: offer.until } : {})}
      onProgress={(ms) => {
        left.current = ms;
        liveDeadline = { key: offer.key, endsAt: Date.now() + ms };
      }}
      onUndo={offer.undo ? () => void run() : null}
      onDone={() => {
        if (state.undo?.key === offer.key) update({ undo: null });
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Nächsten Klärfall suchen

/** Klärfall in der Auftragsliste: Status-Spalte mit dem Wert `case`. */
const CASE_MATCH: ListRowsGroup = { column: 'orders.status', value: 'case' }; // sql-lint: erlaubt Spaltenschlüssel des Listen-Baukastens, kein SQL

/**
 * Nächsten offenen Klärfall der Ansicht suchen: der Server fragt die aktuelle Listenabfrage nach dem nächsten Klärfall
 * nach `current` (gruppiert ab der Herkunftsgruppe `from` über alle gezeigten Gruppen); der Treffer wird frisch geladen
 * (zwei Bearbeiter) – das füllt zugleich den Cache des Datensatzes, das Panel zeigt ihn sofort. Danach bestätigt dieselbe
 * Server-Frage, dass er noch zur Ansicht gehört (`findNextCase`); fiel er während des Ladens heraus, kein Sprung dorthin.
 */
export function useFindNextCase(): (current: string, findNext: ListFindNext | undefined, from: ListRowsGroup | null, position: string | undefined) => Promise<CaseJump<ShownRow>> {
  const queryClient = useQueryClient();
  const { getToken } = useSession();
  return useCallback(
    async (current, findNext, from, position) => {
      // Ohne Server-Frage (nicht aus einer Liste geöffnet): unsicher – lieber nicht springen als außerhalb der Ansicht.
      if (!findNext) return { kind: 'unsure' };
      return findNextCase<ShownRow>(
        async () => {
          // Ab der Stelle vor dem Lösen: die Aktion ändert evtl. den Sortierwert (z. B. Sortierung nach Status).
          const answer = await findNext(current, CASE_MATCH, from, position);
          if (answer.row) return { kind: 'row', key: answer.row.key, target: { row: answer.row, group: answer.group } };
          return answer.truncated ? { kind: 'unsure' } : { kind: 'none' };
        },
        async (key) => {
          const order = await queryClient.fetchQuery({
            queryKey: ['order', key],
            queryFn: () => apiFetch<OrderDetail>(getToken, `/api/v1/orders/${key}`),
            staleTime: 0,
          });
          return order.checks?.pill === 'case';
        },
      );
    },
    [queryClient, getToken],
  );
}
